import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Loader from "_shared/_views/loading/loader";
import { CanceledError } from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/__session/session-context";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import {
  FeeScheduleTemplateOrderParameters,
  FeeScheduleTemplateOrderField,
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import FeeScheduleTemplateQueryAPIService from "work/fee-schedule-templates/_api/queries/fee-schedule-template-query-api-service";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";

const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
}));
const ListItemTextStyled = styled(ListItemText)(({ theme }) => ({
  minWidth: "26ch",
  maxWidth: "26ch",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  padding: theme.spacing(0.25),
}));

type FeeScheduleExistingTemplateProps = {
  templates?: FeeScheduleTemplate[];
  onTemplateSelected: (template: FeeScheduleTemplate) => void;
};

export default function FeeScheduleExistingTemplate(props: Readonly<FeeScheduleExistingTemplateProps>) {
  const { templates, onTemplateSelected } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [existingTemplates, setExistingTemplates] = React.useState<FeeScheduleTemplate[]>([]);
  const [paginatedParameters, setPaginatedParameters] = React.useState<PaginationParameters>();

  const session = useSession();

  const getFeeScheduleTemplates = React.useCallback(
    async (abortController: AbortController) => {
      setIsLoading(true);
      try {
        const entityId = session.user!.legalEntityId;
        if (!entityId) return;

        const filterParameters = new FeeScheduleTemplateFilterParameters({
          entityId,
        });
        const orderParameters = new FeeScheduleTemplateOrderParameters({
          orderBy: FeeScheduleTemplateOrderField.Name,
          orderDir: OrderDirection.Descending,
        });

        const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery({
          filterParameters,
          orderParameters,
          paginationParameters: paginatedParameters ?? PaginationParameters.create({ pageIndex: 0, pageSize: 100 }),
        });

        const feeScheduleService = new FeeScheduleTemplateQueryAPIService(session);
        const templatesJSON = await feeScheduleService.getFeeScheduleTemplatesByQuery(templateQuery, abortController); // TODO: use store
        const templates = templatesJSON.data.map((templateData) => FeeScheduleTemplate.fromJSON(templateData));
        setExistingTemplates(templates);
      } catch (error) {
        if (error instanceof CanceledError) return;
        console.error(error);
        enqueueSnackbar("Failed to load existing fee schedule templates.", {
          variant: "error",
        });
      } finally {
        setPaginatedParameters((prev) => {
          if (prev) {
            return PaginationParameters.create({ pageIndex: prev.pageIndex, pageSize: prev.pageSize });
          }
        });
        setIsLoading(false);
      }
    },
    [session, paginatedParameters]
  );

  useEffect(() => {
    let abortController = new AbortController();
    if (templates) {
      setExistingTemplates(templates);
    } else {
      void getFeeScheduleTemplates(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    }
  }, [getFeeScheduleTemplates, templates]);

  return (
    <ListContainer>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          {(!existingTemplates || existingTemplates.length < 1) && (
            <Typography>No existing fee schedule templates</Typography>
          )}
          {existingTemplates.map((template) => {
            return (
              <ListItem
                key={template.name}
                onClick={() => onTemplateSelected(template)}
                secondaryAction={<Typography>{`Created ${template.created?.format("MM/DD/YY hh:mm A")}`}</Typography>}
              >
                <Tooltip title={`Select Template: ${template.name}`} placement="top-start" arrow>
                  <Button variant="contained" color="inherit">
                    <ListItemTextStyled
                      primary={template.name.length > 18 ? template.name.substring(0, 18) + "..." : template.name}
                      secondary={template.tags.map((tag) => (
                        <Chip key={tag.value} label={tag.value} size="small" />
                      ))}
                    />
                  </Button>
                </Tooltip>
              </ListItem>
            );
          })}
        </>
      )}
    </ListContainer>
  );
}
